import {
  PERMISSION_GROUPS,
  PERMISSION_KEYS,
  type PermissionKey,
  type UserListItemDto,
} from './contractsMirror.js';

/**
 * Pure helpers behind the role cards. Kept out of the component so the arithmetic an
 * admin reads before granting access ("Manager · 27 of 35") can be tested directly.
 *
 * Everything here is derived from the SERVER's `rolePresets`, never from a second copy
 * of the preset held on the client. If the server changes what Manager grants, these
 * numbers move with it and nothing needs editing here.
 */

export type GroupCoverage = 'full' | 'partial' | 'none';

/** Total keys in the canonical list — the denominator on every card. */
export const TOTAL_PERMISSIONS = PERMISSION_KEYS.length;

/**
 * How much of one labelled group a preset covers.
 *
 * 'partial' is deliberately distinct from 'full': collapsing them would tell an admin a
 * role has "Invoices" when it can view but never issue one, which is exactly the kind of
 * confident-but-wrong summary this screen exists to replace.
 */
export function groupCoverage(preset: readonly PermissionKey[], group: readonly PermissionKey[]) {
  const granted = group.filter((permission) => preset.includes(permission)).length;
  if (granted === 0) return 'none' satisfies GroupCoverage;
  return (granted === group.length ? 'full' : 'partial') satisfies GroupCoverage;
}

export interface RoleGroupSummary {
  /** The group's i18n label stem (`users:permissions.groups.<label>`). */
  label: string;
  coverage: GroupCoverage;
  granted: number;
  total: number;
}

/** One row per labelled group, in the canonical group order. */
export function summarizeRole(preset: readonly PermissionKey[]): RoleGroupSummary[] {
  return PERMISSION_GROUPS.map((group) => ({
    label: group.label,
    coverage: groupCoverage(preset, group.permissions),
    granted: group.permissions.filter((permission) => preset.includes(permission)).length,
    total: group.permissions.length,
  }));
}

/**
 * How many per-user overrides this person carries. Drives the "Advanced access — N custom"
 * row, which is the ONLY place overrides surface on the primary screen.
 *
 * A stored `'default'` is not a customization: it means "inherit the role preset", which is
 * what every user does implicitly. Counting it would show "3 custom" for somebody who has
 * been explicitly reset to standard, and the point of the number is to flag genuine drift.
 */
export function countOverrides(user: Pick<UserListItemDto, 'overrides'>): number {
  return Object.values(user.overrides).filter((value) => value === 'allow' || value === 'deny')
    .length;
}
